import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Descriptions,
  Popover,
  Statistic,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getTargetString } from "../../helpers/targetHelpers";
import UpdateTargetApi from "../../api/target-progress/UpdateTargetApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import TargetProgressHistoryApi from "../../api/target-progress/TargetProgressHistoryApi";
import ProgressHistoryTable from "../tables/ProgressHistoryTable";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import TopPlanProgressGraph from "./TopPlanProgressGraph";
import UpdateTargetProgressModal from "../modal/UpdateTargetProgressModal";
import StrategyManagement from "../strategy/StrategyManagement";
import PlanHistoryIcon from "../Plans/PlanHistoryIcon.js";
import SyncPhrmActualsApi from "../../api/plans/SyncPhrmActualsApi";
import { usePermissions } from "../../hooks/usePermissions";


function TopPlanOverview(props) {
  const [submit, setSubmit] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [targetProgress, setTargetProgress] = useState(0);
  const [update, setUpdate] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const positionState = useSelector((state) => state.positionState.value);
  const { hasPhrmPermission } = usePermissions();
  let curLocation = useLocation();

  useEffect(() => {
    setTargetProgress(props.data?.targetAchieved || 0);
    

  }, [curLocation, props.data]);

  async function onFinish(values) {
    setSubmit(true);
    
    // The percentage calculation is now handled in the modal
    // This function receives the already-calculated actual target
    const actualTarget = values.target;
    
    const apiResponse = await UpdateTargetApi(
      props.data?.planId,
      actualTarget, // Already calculated in modal
      positionState?.id
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      if (apiResponse.targetProgress?.approvalStatus === "Approved") {
        setTargetProgress(actualTarget); // Update with actual target
        openNotificationSuccess("bottomRight", `Target updated`);
      } else {
        openNotificationSuccess(
          "bottomRight",
          `Target updated but approval is pending`
        );
      }
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't update target. Try again later."
      );
    }
    setSubmit(false);
    setUpdate(false);
  }

  async function getProgressHistory() {
    setHistoryLoading(true);
    const apiResponse = await TargetProgressHistoryApi(props.data?.planId);
    if (apiResponse.message === "success") {
      setHistory(apiResponse);
      setHistoryLoading(false);
    }
  }

  function expandHistory() {
    setShowHistory(!showHistory);
    if (!history) {
      getProgressHistory();
    }
  }

  async function handleSyncPhrm() {
    try {
      setSyncLoading(true);
      
      const result = await SyncPhrmActualsApi(props.data?.planId);
      
      if (result.success) {
        openNotificationSuccess(`Successfully synced PHRM actuals! Found ${result.data.actualsCount} records.`);
        
        // Update the target progress if we have latest actual data
        if (result.data.latestActual && result.data.latestActual.achievedAmount) {
          setTargetProgress(result.data.latestActual.achievedAmount);
        }
      } else {
        openNotificationError(result.message || 'Failed to sync PHRM actuals');
      }
    } catch (error) {
      openNotificationError('Error syncing PHRM actuals: ' + (error.message || 'Unknown error'));
      console.error('Sync PHRM - Error:', error);
    } finally {
      setSyncLoading(false);
    }
  }

  function getPerformance() {
    let output = 0;

    const targetProgressNum = Number(targetProgress) || 0;
    const planTargetNum = Number(props.data?.target) || 1;
    
    // Check if this is a negative scale plan
    const isNegativeScale = Boolean(
      props.data?.isNegativeScale === true || 
      props.data?.isNegativeScale === 1 || 
      props.data?.isNegativeScale === "1" ||
      props.data?.isNegativeScale === "true"
    );
    
    if (isNegativeScale) {
      // NEGATIVE SCALE LOGIC: Show remaining percentage (inverted)
      // targetProgress represents achieved reduction
      // We want to show remaining percentage
      const achievedPercentage = (targetProgressNum / planTargetNum) * 100;
      output = (100 - achievedPercentage).toFixed(2);
      output = Math.max(output, 0); // Ensure it doesn't go below 0%
    } else {
      // NORMAL SCALE LOGIC: Standard percentage calculation
      output = ((targetProgressNum / planTargetNum) * 100).toFixed(2);
    }

    if (isNaN(output)) {
      output = 0;
    }
    return output;
  }

  function renderAttributes() {
    return props.data?.planAttributeValue
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .map((attribute) => {
        let displayValue = "-";
        
        if (attribute.valueType === "text") {
          displayValue = attribute.text || "-";
        } else if (attribute.valueType === "number") {
          displayValue = attribute.number || "-";
        } else if (attribute.valueType === "date") {
          displayValue = attribute.date 
            ? moment(attribute.date).format("DD.MM.YYYY") 
            : "-";
        } else if (attribute.valueType === "double") {
          displayValue = attribute.double !== null && attribute.double !== undefined
            ? attribute.double.toString()
            : "-";
        } else if (attribute.valueType === "dropdown") {
          // Use the resolved display value from backend
          displayValue = attribute.displayValue || attribute.value || "-";
        } else if (attribute.valueType === "boolean") {
          displayValue = attribute.value === true || attribute.value === "true" ? "Yes" : "No";
        }
        
        return (
          <Descriptions.Item
            key={attribute.id}
            label={attribute.name}
            className="w-1/2"
          >
            {displayValue}
          </Descriptions.Item>
        );
      });
  }

  // Check if plan is inactive
  const isInactive = props.data?.isActive === 0 || props.data?.isActive === false;

  return (
    <div className="pb-8" style={{ 
      opacity: isInactive ? 0.5 : 1,
      pointerEvents: isInactive ? 'none' : 'auto'
    }}>
      {isInactive && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div className="text-yellow-800 font-semibold">⚠️ This plan is currently inactive</div>
          <div className="text-yellow-700 text-sm">Inactive plans do not contribute to progress calculations.</div>
        </div>
      )}
      <div className="flex justify-between items-start">
        <div className="mb-4">
          <div className="flex items-center mb-1">
            <div className="flex items-center mr-2">
              <div
                className="w-2 h-2 mr-1 rounded-full"
                style={{ backgroundColor: props.data?.unit?.orgLevelColor }}
              />
              <div>{props.data?.unit?.name}</div>
              <Popover
                placement="top"
                content={
                  <div className="text-xs">
                    {props.data?.unit?.name}
                    {props.data?.unit?.headUnit &&
                      " < " + props.data?.unit?.headUnit?.name}
                    {props.data?.unit?.headUnit?.headUnit &&
                      " < " + props.data?.unit?.headUnit?.headUnit?.name}
                  </div>
                }
                trigger="hover"
              >
                <div className="text-gray-500 text-xs ml-1">
                  <InfoCircleOutlined />
                </div>
              </Popover>
            </div>
            <Tag>{props.data?.planGroup}</Tag>
            <div className="text-xs text-gray-400">
              {props.data?.targetDate
                ? moment(props.data?.targetDate).format("DD.MM.YYYY")
                : ""}
            </div>
          </div>
          <div className="text-lg">{props.data?.title}</div>
          <div className="max-w-xl">{props.data?.description}</div>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center">
            {props.data?.assignedTo?.imageUrl ? (
              <Avatar
                className="cursor-pointer"
                src={props.data?.assignedTo?.imageUrl}
              />
            ) : (
              <Avatar>
                {props.data?.assignedTo?.firstName ? (
                  props.data?.assignedTo?.firstName?.charAt(0).toUpperCase()
                ) : (
                  <UserOutlined />
                )}
              </Avatar>
            )}
            <div className="ml-2">
              <div>
                {props.data?.assignedTo?.firstName
                  ? props.data?.assignedTo?.firstName +
                  " " +
                  props.data?.assignedTo?.lastName
                  : "-"}
              </div>
              <div className="text-xs -mt-1">
                {props.data?.assignedTo?.designationAbb}
              </div>
            </div>
          </div>
          <div className="mt-2">
            <PlanHistoryIcon
              planId={props.data?.planId}
              planTitle={props.data?.title}
            />
          </div>
        </div>
      </div>
      {props.data?.target !== 0 && props.data?.target && (
        <>
          <div className="mb-4">
            <Statistic
              title="CURRENT PROGRESS"
              value={getPerformance()}
              prefix={
                props.data?.progressStatus === "On Track" ? (
                  <ArrowUpOutlined style={{ color: "green" }} />
                ) : props.data?.progressStatus === "Off Track" ? (
                  <ArrowDownOutlined style={{ color: "orange" }} />
                ) : (
                  <ArrowDownOutlined style={{ color: "red" }} />
                )
              }
              suffix="% completed"
            />
          </div>

          <div className="mb-6 max-w-3xl">
            <div className="border rounded">
              <div
                className={
                  "p-3 flex justify-between items-center " +
                  (showHistory && "border-b")
                }
              >
                <div className="flex items-center">
                  <div>
                    <div className="">Target</div>
                    <div className="flex items-center">
                      <div className="text-lg">
                        {(() => {
                          // Check if this is a negative scale plan
                          const isNegativeScale = Boolean(
                            props.data?.isNegativeScale === true || 
                            props.data?.isNegativeScale === 1 || 
                            props.data?.isNegativeScale === "1" ||
                            props.data?.isNegativeScale === "true"
                          );
                          
                          if (isNegativeScale && targetProgress) {
                            // For negative scale: show progress with negative sign
                            return getTargetString(
                              -Math.abs(targetProgress),
                              props.data?.scale?.scaleName,
                              props.data?.scale?.scaleUnitAbbreviation
                            );
                          } else {
                            return targetProgress
                              ? getTargetString(
                                targetProgress,
                                props.data?.scale?.scaleName,
                                props.data?.scale?.scaleUnitAbbreviation
                              )
                              : "0";
                          }
                        })()}
                      </div>
                      <div className="mx-1 text-lg">/</div>
                      <div className="text-lg">
                        {(() => {
                          // Check if this is a negative scale plan
                          const isNegativeScale = Boolean(
                            props.data?.isNegativeScale === true || 
                            props.data?.isNegativeScale === 1 || 
                            props.data?.isNegativeScale === "1" ||
                            props.data?.isNegativeScale === "true"
                          );
                          
                          if (isNegativeScale) {
                            // For negative scale: show target with negative sign
                            return getTargetString(
                              -Math.abs(props.data?.target),
                              props.data?.scale?.scaleName,
                              props.data?.scale?.scaleUnitAbbreviation
                            );
                          } else {
                            // For normal scale: show target as is
                            return getTargetString(
                              props.data?.target,
                              props.data?.scale?.scaleName,
                              props.data?.scale?.scaleUnitAbbreviation
                            );
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  {props.data?.canUpdate && !props.data?.cascadeFromSplitPlans ? (
                    !update && (
                      <>
                        <Button onClick={() => setUpdate(true)} type="primary">
                          Update
                        </Button>
                        <Tooltip title={"Update History"}>
                          <div
                            className="cursor-pointer text-blue-500 hover:text-blue-600 ml-2"
                            onClick={() => expandHistory()}
                          >
                            <HistoryOutlined />
                          </div>
                        </Tooltip>
                        {/* Temporarily show for all plans for testing - change back to props.data?.ehrmGoalId && later */}
                        {hasPhrmPermission && (
                          <Tooltip title={"Sync actual achievement data from PHRM/EHRM system"}>
                            <div
                              className={`cursor-pointer ml-2 ${syncLoading ? 'text-gray-400' : 'text-green-500 hover:text-green-600'}`}
                              onClick={syncLoading ? null : handleSyncPhrm}
                            >
                              <SyncOutlined spin={syncLoading} />
                            </div>
                          </Tooltip>
                        )}
                      </>
                    )
                  ) : (
                    <>
                      <Popover
                        placement="top"
                        content={
                          <div className="text-xs">
                            {props.data?.cascadeFromSplitPlans 
                              ? "Update disabled - This plan uses split plans for progress"
                              : "You can't update this target"
                            }
                          </div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs mr-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                      <Button disabled type="primary">
                        Update
                      </Button>
                      <Tooltip title={"Update History"}>
                        <div
                          className="cursor-pointer text-blue-500 hover:text-blue-600 ml-2"
                          onClick={() => expandHistory()}
                        >
                          <HistoryOutlined />
                        </div>
                      </Tooltip>
                      {hasPhrmPermission && (
                        <Tooltip title={"Sync actual achievement data from PHRM/EHRM system"}>
                          <div
                            className={`cursor-pointer ml-2 ${syncLoading ? 'text-gray-400' : 'text-green-500 hover:text-green-600'}`}
                            onClick={syncLoading ? null : handleSyncPhrm}
                          >
                            <SyncOutlined spin={syncLoading} />
                          </div>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              </div>
              {showHistory && (
                <div className="max-h-64 overflow-auto custom-scroll">
                  <div className="p-3">
                    <ProgressHistoryTable
                      data={history?.targetProgressHistory}
                      handleOk={() => getProgressHistory()}
                      loading={historyLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* WORKING Strategies Section - the one you created */}
      <div className="mb-6">
        <StrategyManagement
          planId={props.data?.planId}
          planData={props.data}
          positionId={positionState?.id}
        />
      </div>



      <div className="mb-6 max-w-3xl">
        <Descriptions size="middle" column={1} bordered>
          {renderAttributes()}
        </Descriptions>
      </div>
      <Card
        style={{
          borderColor: "#e5e7eb",
          marginBottom: "24px",
        }}
      >
        <div className="text-base pri-font mb-4">Current Progress</div>
        <TopPlanProgressGraph currentProgress={getPerformance()} />
      </Card>
      {update && (
        <UpdateTargetProgressModal
          visible={update}
          handleCancel={() => setUpdate(false)}
          data={props.data}
          targetProgress={targetProgress}
          onFinish={onFinish}
          submit={submit}
        />
      )}
    </div>
  );
}

export default TopPlanOverview;
